import {useMemo} from "react";
import {Area, Line} from "recharts";
import {BaseChart, ChartComponent} from "./BaseChart";
import {TimeSeriesDataProps} from "../../../types/timeSeriesDataProps";
import {FieldResult} from "../../../types/domain/fieldResult";
import {CHART_ITEM_HIDDEN_LABEL} from "./BaseChartTooltip";
import {useTranslation} from "react-i18next";
import dayjs from "dayjs";

export const VirtualTensiometerChart: ChartComponent = ({data}) => {
    const {t} = useTranslation();
    const chartData = useMemo(() => data.map(mapFieldResultsToChartData), [data]);

    return (
        <BaseChart
            title={t("insights.charts.virtual_tensiometer.title")}
            yLabel={t("insights.charts.virtual_tensiometer.y_label")}
            data={chartData}
            helpPopoverTranslationKey="help.popover.fields.virtual_tensiometer"
        >
            <Area
                type="monotone"
                dataKey="soilWaterPotentialFieldCapacity"
                name={t("insights.charts.virtual_tensiometer.area1_name")}
                unit={t("insights.charts.virtual_tensiometer.y_label")}
                yAxisId="left"
                fill="#C2FCC0"
                strokeDasharray="5 5"
                strokeWidth={0}
                isAnimationActive={false}
            />
            <Area
                type="monotone"
                dataKey="soilWaterPotentialCritical"
                name={t("insights.charts.virtual_tensiometer.area2_name")}
                unit={t("insights.charts.virtual_tensiometer.y_label")}
                yAxisId="left"
                stroke="#EA463C"
                fill="#EA463C"
                strokeWidth={2}
                isAnimationActive={false}
                baseValue="dataMin"
            />
            <Area
                type="monotone"
                dataKey="soilWaterPotentialSaturated"
                name={CHART_ITEM_HIDDEN_LABEL}
                unit={t("insights.charts.virtual_tensiometer.y_label")}
                legendType="none"
                yAxisId="left"
                stroke="#A1AEE3"
                fill="#E2E7F8"
                strokeDasharray="5 5"
                strokeWidth={2}
                isAnimationActive={false}
            />
            <Area
                type="monotone"
                dataKey="soilWaterPotentialPercentiles"
                name={CHART_ITEM_HIDDEN_LABEL}
                unit={t("insights.charts.virtual_tensiometer.y_label")}
                legendType="none"
                yAxisId="left"
                stroke="#545A6E"
                fill="#545A6E"
                opacity="0.35"
                isAnimationActive={false}
            />
            <Line
                type="monotone"
                dataKey="soilWaterPotentialRootZoneCombined"
                name={t("insights.charts.virtual_tensiometer.line1_name")}
                unit={t("insights.charts.virtual_tensiometer.y_label")}
                yAxisId="left"
                stroke="#5161C1"
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
            />
        </BaseChart>
    );
};

const mapFieldResultsToChartData = (r: FieldResult): DataProps => ({
    date: dayjs(r.date).toDate(),
    soilWaterPotentialCritical: r.aggregatedProps.soil_water_potential_critical,
    soilWaterPotentialRootZoneCombined: r.aggregatedProps.soil_water_potential_root_zone_combined,
    soilWaterPotentialFieldCapacity: [
        r.aggregatedProps.soil_water_potential_critical,
        r.aggregatedProps.soil_water_potential_fc,
    ],
    soilWaterPotentialSaturated: r.aggregatedProps.soil_water_potential_fc,
    soilWaterPotentialPercentiles: [
        r.aggregatedProps.soil_water_potential_percentile5,
        r.aggregatedProps.soil_water_potential_percentile95,
    ],
});

export interface DataProps extends TimeSeriesDataProps {
    soilWaterPotentialCritical: number;
    soilWaterPotentialRootZoneCombined: number;
    soilWaterPotentialFieldCapacity: number[];
    soilWaterPotentialSaturated: number;
    soilWaterPotentialPercentiles: number[];
}
